/**
 * DepotUploadSection - Depot load impact & cost analysis
 *
 * Three-step flow:
 * 1. Upload a load profile CSV (8760 hourly or 35040 quarter-hourly)
 * 2. Select a tariff via UnifiedTariffSelector
 * 3. Click "Analyze Cost" to get cost breakdown
 */

import React, { useCallback, useState } from 'react';
import {
    Box,
    Alert,
    Button,
    CircularProgress,
    Typography,
} from '@mui/material';
import {
    DataUsage as DataUsageIcon,
    Assessment as AnalyzeIcon,
} from '@mui/icons-material';
import {
    SectionCard,
    useAuth,
    API_BASE_URL,
    getAuthHeaders,
    vortxColors,
    UnifiedTariffSelector,
} from '@vortx/shared-ui';
import { DepotUploader } from '../../../components/ev';

function DepotUploadSection({ projectId, depotConfig, onComplete }) {
    const { currentUser } = useAuth();

    // Load profile state
    const [loadData, setLoadData] = useState(null);

    // Tariff state
    const [selectedTariff, setSelectedTariff] = useState(depotConfig?.tariff_data || null);

    // Analysis state
    const [analyzing, setAnalyzing] = useState(false);
    const [error, setError] = useState(null);

    const handleUploadComplete = useCallback((result) => {
        setLoadData(result);
        setError(null);
    }, []);

    const handleTariffSelect = useCallback((tariff) => {
        // Unwrap tariff data if it's from Flux Rate Database schema
        let tariffDataToUse = tariff;
        if (tariff.rate_data && tariff.rate_data.rate_data) {
            tariffDataToUse = tariff.rate_data.rate_data;
        }
        // Preserve metadata
        tariffDataToUse = {
            ...tariffDataToUse,
            id: tariff.id,
            name: tariff.name || tariff.label,
            utility_name: tariff.utility_name || tariffDataToUse.utility_name,
            rate_schedule_name: tariff.rate_schedule_name || tariffDataToUse.rate_schedule_name,
        };
        setSelectedTariff(tariffDataToUse);
    }, []);

    const handleAnalyze = useCallback(async () => {
        if (!loadData?.load_kw) {
            setError('Upload a load profile first');
            return;
        }
        if (!selectedTariff) {
            setError('Select a tariff first');
            return;
        }

        setAnalyzing(true);
        setError(null);

        try {
            const headers = await getAuthHeaders(currentUser);
            headers['Content-Type'] = 'application/json';

            // Step 1: Analyze cost
            const response = await fetch(
                `${API_BASE_URL}/api/flux_ev/v1/ev/depot/analyze-cost`,
                {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        load_kw: loadData.load_kw,
                        interval_minutes: loadData.interval_minutes,
                        tariff_data: selectedTariff,
                    }),
                },
            );

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Analysis failed');
            }

            // Step 2: Save to project
            const saveResponse = await fetch(
                `${API_BASE_URL}/api/flux_ev/v1/ev/depot/save-to-project`,
                {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        project_id: projectId,
                        cost_analysis: data,
                        tariff_data: selectedTariff,
                        interval_minutes: loadData.interval_minutes,
                        data_points: loadData.data_points,
                    }),
                },
            );

            if (!saveResponse.ok) {
                const saveErr = await saveResponse.json();
                throw new Error(saveErr.error || 'Failed to save results');
            }

            if (onComplete) onComplete(data);
        } catch (err) {
            setError(err.message || 'Analysis failed');
        } finally {
            setAnalyzing(false);
        }
    }, [loadData, selectedTariff, currentUser, projectId, onComplete]);

    const hasExistingResults = !!depotConfig?.cost_analysis;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Step 1: Upload */}
            <SectionCard title="Upload Load Profile" icon={<DataUsageIcon />}>
                {hasExistingResults && !loadData && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Cost analysis already complete. Upload a new file to re-analyze.
                    </Alert>
                )}
                {!hasExistingResults && !loadData && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Upload a full year of load data (CSV): 8760 hourly or 35040 quarter-hourly kW values.
                    </Alert>
                )}
                <DepotUploader onComplete={handleUploadComplete} />

                {loadData && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        Loaded {loadData.data_points.toLocaleString()} data points
                        ({loadData.interval_minutes}-min intervals).
                        Peak: {loadData.peak_kw} kW | Total: {Math.round(loadData.total_kwh).toLocaleString()} kWh/yr
                    </Alert>
                )}
            </SectionCard>

            {/* Step 2: Tariff Selection */}
            <SectionCard title="Select Tariff" icon={<AnalyzeIcon />}>
                <UnifiedTariffSelector
                    selectedTariff={selectedTariff}
                    onTariffSelect={handleTariffSelect}
                />
                {selectedTariff && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Selected: {selectedTariff.name || selectedTariff.rate_schedule_name || 'Custom tariff'}
                    </Typography>
                )}
            </SectionCard>

            {/* Step 3: Analyze */}
            {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="contained"
                    onClick={handleAnalyze}
                    disabled={analyzing || !loadData || !selectedTariff}
                    startIcon={analyzing ? <CircularProgress size={18} color="inherit" /> : <AnalyzeIcon />}
                    sx={{
                        bgcolor: vortxColors.teal,
                        '&:hover': { bgcolor: vortxColors.darkGreen },
                        minWidth: 200,
                    }}
                >
                    {analyzing ? 'Analyzing...' : 'Analyze Cost'}
                </Button>
            </Box>
        </Box>
    );
}

export default DepotUploadSection;
